import React, { useState } from 'react';
import { IconButton, Tooltip, Badge } from '@mui/material';
import {
  Help as HelpIcon,
  Notifications as NotificationsIcon,
  DarkModeOutlined,
  LightModeOutlined
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../../contexts/ThemeProvider';
import NotificationPanel from '../../NotificationPanel';

interface NavigationIconsProps {
  showHelp?: boolean;
  showNotifications?: boolean;
  showThemeToggle?: boolean;
  iconSize?: 'small' | 'medium';
  spacing?: number;
  variant?: 'default' | 'small';
}

const NavigationIcons: React.FC<NavigationIconsProps> = ({
  showHelp = false,
  showNotifications = true,
  showThemeToggle = true,
  iconSize = 'medium',
  spacing = 0.5,
  variant = 'default'
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { mode, toggleColorMode } = useThemeMode();
  
  const [notificationAnchor, setNotificationAnchor] = useState<HTMLElement | null>(null);

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleViewAllNotifications = () => {
    navigate('/notifications');
    setNotificationAnchor(null);
  };

  const iconSx = variant === 'small' ? {
    color: theme.palette.text.secondary,
    '&:hover': { 
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.primary.main
    },
  } : {
    mx: spacing,
    color: theme.palette.primary.main,
    '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.06)' }
  };

  return (
    <>
      {showHelp && (
        <Tooltip title="Help">
          <IconButton size={iconSize} sx={iconSx}>
            <HelpIcon fontSize={iconSize} />
          </IconButton>
        </Tooltip>
      )}
      
      {showNotifications && (
        <>
          <NotificationPanel
            anchorEl={notificationAnchor}
            open={Boolean(notificationAnchor)}
            onClose={handleNotificationClose}
            onViewAll={handleViewAllNotifications}
            onNotificationClick={handleNotificationClick}
            iconSize={iconSize}
            iconSx={iconSx}
          />
        </>
      )}

      {showThemeToggle && (
        <Tooltip title={mode === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"}>
          <IconButton
            onClick={toggleColorMode}
            size={iconSize}
            sx={iconSx}
          >
            {mode === "light" ? 
              <DarkModeOutlined fontSize={iconSize} /> : 
              <LightModeOutlined fontSize={iconSize} />
            }
          </IconButton>
        </Tooltip>
      )}
    </>
  );
};

export default NavigationIcons;